import deepmerge from 'deepmerge';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  FlightOption,
  ItineraryStatus,
  OptionTypes,
  SaviSelectIntlCombinations,
  SegmentResponse,
  TravellerDetails,
} from '../../../models/itinerary';
import { AppState } from '../../model';
import { getOptionType } from '../segment-options/selectors';
import {
  AirSegmentResponse,
  CarOption,
  CarSegmentResponse,
  HotelOption,
  HotelSegmentResponse,
  SegmentTypes,
} from './../../../models/itinerary';
import { getCombinationsForSaviSelectIntl } from './saviSelectIntlSelector';

/* -------------------------------------------------------------------------- */
/*                                   Status                                   */
/* -------------------------------------------------------------------------- */

/**
 * Get itinerary status
 *
 * @param state
 */
export const getStatus = (state: AppState): ItineraryStatus | undefined =>
  state.itinerary.data.status;

export const isSaviSelectIntl = (state: AppState): boolean =>
  state.itinerary.data.saviSelectIntl;

/**
 * Check if itinerary status matches a status value
 */
export const isStatus = (status: ItineraryStatus) =>
  createSelector(
    getStatus,
    (currentStatus) => currentStatus === status,
  );

/**
 * Check if itinerary is a new itinerary that needs to be accepted/reviewed
 */
export const isNew = isStatus('New');

/**
 * Check if itinerary has been rejected
 */
export const isRejected = isStatus('Rejected');

/**
 * Check if itinerary has been accepted
 */
export const isAccepted = isStatus('Accepted');

/**
 * Check if itinerary has been accepted with proposed changes
 */
export const isAcceptedWithChanges = isStatus('AcceptedWithChanges');

/* -------------------------------------------------------------------------- */
/*                                Loading state                               */
/* -------------------------------------------------------------------------- */

/**
 * Check if itinerary data is loading from the server
 */
export const isLoading = (state: AppState): boolean => state.itinerary.fetching;

/**
 * Check if there was an error loading the itinerary data from the server
 */
export const getError = (state: AppState): any => state.itinerary.error;

/* -------------------------------------------------------------------------- */
/*                            Itinerary information                           */
/* -------------------------------------------------------------------------- */

/**
 * Check if travel arranger has decided to let traveller view itinerary option pricing
 */
export const shouldShowPricing = (state: AppState): boolean =>
  state.itinerary.data.includePricing;

/**
 * Get start date for itinerary
 */
export const getStartDate = (state: AppState) => state.itinerary.data.fromDate;

/**
 * Get end date for itinerary
 */
export const getEndDate = (state: AppState) => state.itinerary.data.toDate;

/**
 * Get list of destinations
 */
export const getDestinations = (state: AppState): string[] =>
  state.itinerary.data.destinations;

/* -------------------------------------------------------------------------- */
/*                                  Segments                                  */
/* -------------------------------------------------------------------------- */

/**
 * Get list of segments for the itinerary
 */
export const getSegments = (state: AppState): SegmentResponse[] =>
  state.itinerary.data.itinerary;

/**
 * Check if there are itinerary segments loaded
 */
export const hasSegments = createSelector(
  getSegments,
  getCombinationsForSaviSelectIntl,
  isSaviSelectIntl,
  (
    itinerary: SegmentResponse[],
    combinations: SaviSelectIntlCombinations,
    isSaviSelectIntl: boolean,
  ) =>
    isSaviSelectIntl
      ? combinations['Flight'].length > 0 ||
        combinations['Hotel'].length > 0 ||
        combinations['Car'].length > 0
      : itinerary.length > 0,
);

/**
 * Find segment by segment id
 */
export const findSegment = (segmentId: string | number) =>
  createSelector(
    getSegments,
    (itinerary) =>
      itinerary.find(({ orderId }) => String(orderId) === String(segmentId)),
  );

/**
 * Group segments by day
 */
const groupSegmentsByDay = (itinerary: SegmentResponse[]) => {
  const getDate = (segmentResponse: SegmentResponse): Date => {
    switch (segmentResponse.segmentType) {
      case 'Air':
        return segmentResponse.airSegment!.departureDate;
      case 'Hotel':
        return segmentResponse.hotelSegment!.checkIn;
      case 'Car':
        return segmentResponse.carSegment!.pickupDate;
    }
  };

  const getFormattedDate = (segmentResponse: SegmentResponse): string => {
    return moment(getDate(segmentResponse)).format('YYYY-MM-DD');
  };

  return itinerary.reduce(
    (previous: Record<string, SegmentResponse[]>, current: SegmentResponse) => {
      return deepmerge(previous, {
        [getFormattedDate(current)]: [current.orderId],
      });
    },
    {},
  );
};

/**
 * Get list of segments grouped by day
 */
export const getSegmentsGroupedByDay = createSelector(
  getSegments,
  groupSegmentsByDay,
);

/**
 * Get SegmentResponse by segment id
 */
export const getSegmentResponse = (segmentId: string | number) =>
  createSelector(
    findSegment(segmentId),
    (
      segmentResponse,
    ):
      | undefined
      | AirSegmentResponse
      | CarSegmentResponse
      | HotelSegmentResponse => {
      if (!segmentResponse) return undefined;

      switch (segmentResponse.segmentType) {
        case 'Air':
          return segmentResponse.airSegment;
        case 'Car':
          return segmentResponse.carSegment;
        case 'Hotel':
          return segmentResponse.hotelSegment;
      }
    },
  );

/**
 * Determine type of segment, given segment id (e.g. 'Air', 'Hotel' or 'Car')
 */
export const getSegmentType = (segmentId: string | number) =>
  createSelector(
    findSegment(segmentId),
    (segment): SegmentTypes => (!!segment ? segment.segmentType : 'Air'),
  );

/* -------------------------------------------------------------------------- */
/*                               Travel Arranger                              */
/* -------------------------------------------------------------------------- */

/**
 * Get travel-arranger information
 */
export const getTravelArranger = (state: AppState) =>
  state.itinerary.data.travelArranger;

/**
 * Get travel-arranger's first name
 */
export const getTravelArrangerName = createSelector(
  getTravelArranger,
  (travelArranger) => travelArranger.firstName,
);

/**
 * Get itinerary notes from travel-arranger
 */
export const getTravelArrangerNotes = (state: AppState) =>
  state.itinerary.data.arrangerNotes;

/* -------------------------------------------------------------------------- */
/*                                   Options                                  */
/* -------------------------------------------------------------------------- */

export const getOptionIdFromOption = (option: OptionTypes): string => {
  switch (getOptionType(option)) {
    case 'Hotel':
      return (option as HotelOption).hotelId.toString();
    case 'Car':
      return (option as CarOption).supplierCode;
    case 'Air':
    default:
      return (option as FlightOption).flightId.toString();
  }
};

/* -------------------------------------------------------------------------- */
/*                                 Travellers                                 */
/* -------------------------------------------------------------------------- */

/**
 * Get list of travellers
 */
export const getTravellers = (state: AppState): TravellerDetails[] =>
  state.itinerary.data.travellers;

/**
 * Get list of travellers' full-names
 */
export const getTravellerNames = createSelector(
  getTravellers,
  (travellers: TravellerDetails[]) =>
    travellers.map((traveller) => traveller.fullName),
);

/**
 * Get number of travellers
 */
export const getTravellerCount = createSelector(
  getTravellers,
  (travellers: TravellerDetails[]): number => travellers.length,
);
